import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Container,
  Alert,
  Snackbar,
  Fade,
  // useTheme, // Removed as not used
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  LockReset as LockResetIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { theme as appTheme } from '../../theme/theme';

interface ForgotPasswordScreenProps {}

const ForgotPasswordScreen: React.FC<ForgotPasswordScreenProps> = () => {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  // 本页面统一使用中文文案，移除多语言依赖
  
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const handleResetPassword = async () => {
    if (!email.trim()) {
      setSnackbar({
        open: true,
        message: '请输入邮箱地址',
        severity: 'error',
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSnackbar({
        open: true,
        message: '请输入有效的邮箱地址',
        severity: 'error',
      });
      return;
    }

    try {
      setIsLoading(true);
      await resetPassword(email);
      setEmailSent(true);
      setSnackbar({
        open: true,
        message: '密码重置说明已发送',
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || '发送重置邮件失败',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${appTheme.colors.primary} 0%, ${appTheme.colors.accent} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          pt: { xs: 3, sm: 4 },
          pb: 2,
          px: { xs: 2, sm: 3 },
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            position: 'absolute',
            left: { xs: 8, sm: 16 },
            color: 'white',
            padding: { xs: 1, sm: 1.5 },
            '&:hover': {
              backgroundColor: alpha('#FFFFFF', 0.1),
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: 'white',
              mb: 1,
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            重置密码
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#F5F5DC',
              textAlign: 'center',
              lineHeight: 1.4,
              px: { xs: 1, sm: 2 },
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            }}
          >
            请输入您的邮箱以获取重置说明
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', py: { xs: 3, sm: 4 } }}>
        <Fade in timeout={600}>
          <Card
            elevation={8}
            sx={{
              width: '100%',
              borderRadius: 5,
              overflow: 'auto',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <LockResetIcon
                  sx={{
                    fontSize: { xs: 48, sm: 60 },
                    color: appTheme.colors.primary,
                    mb: 2,
                  }}
                />
              </Box>
              
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: appTheme.colors.primary,
                  mb: 2,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                }}
              >
                忘记密码
              </Typography>
              
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 1.6,
                  mb: { xs: 3, sm: 4 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  px: { xs: 1, sm: 0 },
                }}
              >
                别担心，输入与账户关联的邮箱，我们将发送重置说明。
              </Typography>
              
              {!emailSent ? (
                <Box>
                  <TextField
                    fullWidth
                    label={'邮箱地址'}
                    type="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    variant="outlined"
                    autoComplete="email"
                    InputProps={{
                      startAdornment: (
                        <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{
                      mb: { xs: 2, sm: 3 },
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: appTheme.colors.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: appTheme.colors.primary,
                      },
                    }}
                  />
                  
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleResetPassword}
                    disabled={isLoading}
                    sx={{
                      mb: { xs: 2, sm: 3 },
                      py: { xs: 1.25, sm: 1.5 },
                      borderRadius: 3,
                      backgroundColor: appTheme.colors.primary,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      '&:hover': {
                        backgroundColor: alpha(appTheme.colors.primary, 0.8),
                      },
                      '&:disabled': {
                        backgroundColor: alpha(appTheme.colors.primary, 0.5),
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      '发送重置说明'
                    )}
                  </Button>
                </Box>
              ) : (
                <Fade in timeout={600}>
                  <Box>
                    <CheckCircleIcon
                      sx={{
                        fontSize: { xs: 48, sm: 60 },
                        color: '#4CAF50',
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#4CAF50',
                        mb: 1,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      邮件已发送
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        mb: { xs: 3, sm: 4 },
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        px: { xs: 1, sm: 0 },
                      }}
                    >
                      {`重置说明已发送到：${email}`}
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleBackToLogin}
                      sx={{
                        mb: { xs: 2, sm: 3 },
                        py: { xs: 1.25, sm: 1.5 },
                        borderRadius: 3,
                        backgroundColor: appTheme.colors.primary,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover': {
                          backgroundColor: alpha(appTheme.colors.primary, 0.8),
                        },
                      }}
                    >
                      返回登录
                    </Button>
                  </Box>
                </Fade>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  已经记起密码了？{' '}
                </Typography>
                <Button
                  variant="text"
                  onClick={handleBackToLogin}
                  sx={{
                    color: appTheme.colors.primary,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    p: 0,
                    ml: 0.5,
                    minWidth: 'auto',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  去登录
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ForgotPasswordScreen;